import React from 'react';
import { ChevronLeft, Download, ChevronDown, FileText, FileType, Presentation, Loader2, RefreshCcw, Palette, BookmarkPlus, History, Info } from 'lucide-react';
import ProposalProgressBar from '../../rfp/team-lead/analysis/components/Footer/ProposalProgressBar';
import { ClientDecisionControl } from '../../rfp/team-lead/analysis/components/RFPAnalysisHeader';
import api from '../../../services/api';

const getActiveStep = (rfpData) => {
    if (!rfpData) return 1;
    const currentCode = rfpData.global_status?.code ?? rfpData.status ?? 0;
    const stageName = String(rfpData.global_status?.label || rfpData.proposal_stage || '').toLowerCase();
    if (stageName.includes('submitted') || currentCode === 5) return 4;
    if (stageName.includes('finalised') || stageName.includes('finalized') || currentCode === 4) return 3;
    if (stageName.includes('review') || stageName.includes('in-progress') || currentCode === 2 || currentCode === 3) return 2;
    return 1;
};




/**
 * PPTTopNav — Top navigation header: Back, title, progress stepper, and Download Option.
 */
const PPTTopNav = ({
    onBack,
    navigate,
    setShowExportModal,
    rfpId,
    rfpData,
    executeStageTransition,
    fetchRFPDetails,
    showToast,
    brandingData,
    onApplyBranding,
    onSaveCheckpoint,
    onVersionHistory,
    currentVersionNumber = 6,
    draftCount = 0
}) => {
    const isNoGo = rfpData?.global_status?.code === 9 ||
        String(rfpData?.proposal_stage || '').toUpperCase().includes('NO_GO');

    const isSubmitted = String(rfpData?.global_status?.label || rfpData?.proposal_stage || '').toLowerCase().includes('submitted') ||
        rfpData?.global_status?.code === 5 ||
        getActiveStep(rfpData) === 4;

    const [isRegenerating, setIsRegenerating] = React.useState(false);

    const handleRegeneratePPT = async () => {
        setIsRegenerating(true);
        try {
            const formData = new FormData();
            formData.append('rfp_id', rfpId);

            const response = await api.post(`ppt-config/rfp/${rfpId}/generate-slides`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.status === 200 || response.status === 201) {
                window.location.reload();
            }
        } catch (err) {
            console.error('Generation error:', err);
            const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to regenerate presentation.';
            if (showToast) {
                showToast(errMsg, 'error');
            } else {
                alert(errMsg);
            }
        } finally {
            setIsRegenerating(false);
        }
    };

    const [showMoreMenu, setShowMoreMenu] = React.useState(false);
    const menuRef = React.useRef(null);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMoreMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleMenuAction = (action) => {
        setShowMoreMenu(false);
        if (action === 'checkpoint') onSaveCheckpoint();
        if (action === 'history') onVersionHistory();
        if (action === 'regenerate') handleRegeneratePPT();
    };

    return (
        <header className="ppt-top-nav">
            <div className="nav-left" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <button className="icon-btn-back" onClick={() => onBack ? onBack() : navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className="nav-title" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0F172A' }}>Proposal Customization</h2>
                </div>
            </div>

            <div className="nav-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', position: 'relative' }}>
                {rfpData && (
                    <>
                        {/* Proposal Stage Progress Bar with Submitted enabled! */}
                        <ProposalProgressBar
                            activeStep={getActiveStep(rfpData)}
                            currentStageLabel={rfpData?.global_status?.label || rfpData?.proposal_stage}
                            rfpId={rfpId}
                            fetchOverview={fetchRFPDetails}
                            isManager={true}
                            enableSubmittedClick={true}
                        />

                        {/* Proposal Decision Dropdown - Only seen/enabled when stage is Submitted */}
                        {!isNoGo && executeStageTransition && isSubmitted && (
                            <ClientDecisionControl
                                currentStage={rfpData?.global_status?.label || rfpData?.proposal_stage}
                                executeStageTransition={executeStageTransition}
                            />
                        )}
                    </>
                )}

                {/* More Options Dropdown */}
                <div style={{ position: 'relative' }} ref={menuRef}>
                    <button
                        className="regenerate-ppt-btn"
                        onClick={() => setShowMoreMenu(!showMoreMenu)}
                        disabled={isRegenerating}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', background: '#FFFFFF',
                            border: '1.5px solid #E2E8F0', borderRadius: '6px',
                            color: '#64748B', fontWeight: '600', fontSize: '0.85rem',
                            cursor: isRegenerating ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)', whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => { if (!isRegenerating) { e.currentTarget.style.background = '#F8F9FA'; e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.color = '#334155'; } }}
                        onMouseLeave={(e) => { if (!isRegenerating) { e.currentTarget.style.background = '#FFFFFF'; e.currentTarget.style.borderColor = '#E2E8F0'; e.currentTarget.style.color = '#64748B'; } }}
                    >
                        <span>More Options</span>
                        <ChevronDown size={16} style={{ transition: 'transform 0.2s', transform: showMoreMenu ? 'rotate(180deg)' : 'rotate(0)' }} />
                    </button>

                    {showMoreMenu && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '4px',
                            background: '#FFFFFF',
                            border: '1px solid #E2E8F0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(15, 23, 42, 0.1)',
                            minWidth: '180px',
                            zIndex: 100,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '4px'
                        }}>
                            <button
                                onClick={() => handleMenuAction('history')}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
                                    border: 'none', background: 'transparent', width: '100%',
                                    textAlign: 'left', cursor: 'pointer', color: '#334155',
                                    fontSize: '0.85rem', fontWeight: '500', borderRadius: '4px',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#F1F5F9'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                <History size={16} color="#64748B" /> Version Control
                            </button>
                            <button
                                onClick={() => handleMenuAction('checkpoint')}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
                                    border: 'none', background: 'transparent', width: '100%',
                                    textAlign: 'left', cursor: 'pointer', color: '#334155',
                                    fontSize: '0.85rem', fontWeight: '500', borderRadius: '4px',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#F1F5F9'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            >
                                <BookmarkPlus size={16} color="#64748B" /> Save Checkpoint
                            </button>
                            <button
                                onClick={() => handleMenuAction('regenerate')}
                                disabled={isRegenerating}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
                                    border: 'none', background: 'transparent', width: '100%',
                                    textAlign: 'left', cursor: isRegenerating ? 'not-allowed' : 'pointer', color: '#334155',
                                    fontSize: '0.85rem', fontWeight: '500', borderRadius: '4px',
                                    transition: 'background 0.2s', opacity: isRegenerating ? 0.6 : 1
                                }}
                                onMouseEnter={(e) => { if (!isRegenerating) e.currentTarget.style.background = '#F1F5F9'; }}
                                onMouseLeave={(e) => { if (!isRegenerating) e.currentTarget.style.background = 'transparent'; }}
                            >
                                {isRegenerating ? <Loader2 size={16} className="animate-spin" color="#64748B" /> : <RefreshCcw size={16} color="#64748B" />} 
                                Regenerate
                            </button>
                        </div>
                    )}
                </div>

                <button
                    className="btn-primary-gradient download-trigger"
                    onClick={() => setShowExportModal(true)}
                    disabled={isRegenerating}
                >
                    <Download size={18} />
                    <span>Export</span>
                </button>
            </div>

            {/* Screen Freezing Loader Modal for PPT Regeneration */}
            {isRegenerating && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(3px)',
                    zIndex: 99999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <div style={{
                        background: '#FFFFFF',
                        padding: '28px 40px',
                        borderRadius: '16px',
                        boxShadow: '0 20px 50px rgba(15, 23, 42, 0.1)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '16px',
                        border: '1px solid #E2E8F0'
                    }}>
                        <Loader2 size={36} className="animate-spin" color="#FF5526" />
                        <div style={{ textAlign: 'center' }}>
                            <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '600', color: '#1E293B', fontFamily: 'system-ui, sans-serif' }}>
                                Regenerating Presentation
                            </h3>
                            <p style={{ margin: 0, fontSize: '13px', color: '#64748B', fontFamily: 'system-ui, sans-serif' }}>
                                Please wait while we process the slides.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default PPTTopNav;
